import { camelCase, startCase } from "lodash";

import { Config, useConfig } from "@/hooks/useConfig";
import { Layer } from "@/Utils/hierarchy";
import { getMappedName } from "@/Utils/mapping-utils";

// Specific mapping for common business terms
const BUSINESS_TERM_MAPPING = (config: Config): Record<string, string> => ({
  brand_marketing_sale: `${config.getLayerLabel(Layer.Brand)} Marketing Sales`,
  category_marketing_sale: `${config.getLayerLabel(Layer.Category)} Marketing Sales`,
  media_sale: `${config.getLayerLabel(Layer.Product)} Marketing Sales`,
  base_sale: "Base Sales",
  price_sale: "Price Sales",
  error_sale: "Unattributed Sales",
  control_sale: "External Factors",
  owned_media: "Owned Sales",
});

/**
 * Maps raw data names to clean, readable display names
 * @param rawName - The raw name from the data (e.g., "error_sale", "brand_marketing_sale")
 * @param config - The config object containing layer labels
 * @returns Clean, readable name (e.g., "Unattributed Sale", "Brand Marketing Sale")
 */
const mapBusinessTermName = (rawName: string, config: Config): string => {
  // First check if we have a specific mapping
  const specificMapping = BUSINESS_TERM_MAPPING(config)[rawName];
  if (specificMapping) {
    return specificMapping;
  }

  // Try the existing getMappedName function as fallback
  const existingMapping = getMappedName(rawName);
  if (existingMapping) {
    return existingMapping;
  }

  // General transformation: remove underscores and apply proper case
  return startCase(camelCase(rawName));
};

/**
 * Transforms a list of raw business terms to clean, readable names
 * @param rawNames - Array of raw names
 * @param config - The config object containing layer labels
 * @returns Array of clean, readable names
 */
const mapBusinessTermNames = (rawNames: string[], config: Config): string[] => {
  return rawNames.map((name) => mapBusinessTermName(name, config));
};

/**
 * Creates a mapping object from raw names to clean names
 * @param rawNames - Array of raw names
 * @param config - The config object containing layer labels
 * @returns Object with raw names as keys and clean names as values
 */
const createBusinessTermMappingObject = (
  rawNames: string[],
  config: Config
): Record<string, string> => {
  return rawNames.reduce(
    (acc, rawName) => {
      acc[rawName] = mapBusinessTermName(rawName, config);
      return acc;
    },
    {} as Record<string, string>
  );
};

/**
 * Hook that provides business term mapping functions with config injected
 * @returns Object containing mapping functions
 */
export const useBusinessTermMapping = () => {
  const { config } = useConfig();

  return {
    mapBusinessTermName: (rawName: string) => mapBusinessTermName(rawName, config),
    mapBusinessTermNames: (rawNames: string[]) => mapBusinessTermNames(rawNames, config),
    createBusinessTermMappingObject: (rawNames: string[]) =>
      createBusinessTermMappingObject(rawNames, config),
  };
};
